import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  SelectChangeEvent,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Stack,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Add as AddIcon, Upload as UploadIcon, Image as ImageIcon, Link as LinkIcon, Visibility as VisibilityIcon, FactCheck as StructuredIcon, ShortText as UnstructuredIcon, Verified as VerifiedIcon, Storage as StorageIcon, Restaurant as RestaurantIcon, Language as SiteIcon } from '@mui/icons-material';
import {
  FoodResponseDto,
  GetFoodsParams,

  FoodCategory,
  FoodSourceType,
  MergeFoodDto,
  MeasurementUnit,
  FoodSortBy,
  SortOrder,
} from '@services/food.service.ts';
import { adminFoodService, ImportFoodsResponseDto, ImportSessionDto, FoodOptionsResponseDto, FoodDetailDto } from '@services/admin-food.service.ts';
import { useDebounce } from '@hooks/useDebounce.ts';
import { toastService } from '@services/toast.service.ts';
import { ConfirmationDialog } from '@components/common/ConfirmationDialog.tsx';
import { ImportDialog } from '@components/admin/lib/ImportDialog.tsx';
import { MergeDialog } from '@components/admin/lib/MergeDialog.tsx';
import { FoodDialog } from '@components/admin/lib/FoodDialog.tsx';
import { FoodDetailDialog } from '@components/admin/lib/FoodDetailDialog.tsx';
import { FoodSiteContentDialog } from '@components/admin/lib/FoodSiteContentDialog.tsx';
import { LibraryFilterPanel } from '@components/admin/lib/LibraryFilterPanel.tsx';
import { LibraryTable, Column } from '@components/admin/lib/LibraryTable.tsx';
import { TranslationStatus } from '@components/common/TranslationStatus.tsx';
import { getImageUrl } from '@utils/image-url.util.ts';

const STORAGE_KEY = 'admin_food_filters';

export const AdminFoodTab: React.FC = () => {
  // Initialize state from localStorage
  const getInitialState = () => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch (e) {
      return null;
    }
  };

  const savedState = getInitialState();

  const [foods, setFoods] = useState<FoodResponseDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(savedState?.page || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [limit, setLimit] = useState(10);

  const [searchQuery, setSearchQuery] = useState(savedState?.searchQuery || '');
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  const [categoryFilter, setCategoryFilter] = useState<string>(savedState?.categoryFilter || '');
  const [sourceTypeFilter, setSourceTypeFilter] = useState<FoodSourceType>(savedState?.sourceTypeFilter || FoodSourceType.ALL);
  const [expertFilter, setExpertFilter] = useState<number | ''>(savedState?.expertFilter || '');
  const [structuredFilter, setStructuredFilter] = useState<string>(savedState?.structuredFilter || 'all');
  const [sitePublishedFilter, setSitePublishedFilter] = useState<string>(savedState?.sitePublishedFilter || 'all');

  // Sorting state
  const [sortBy, setSortBy] = useState<FoodSortBy>(savedState?.sortBy || 'name');
  const [sortOrder, setSortOrder] = useState<SortOrder>(savedState?.sortOrder || 'ASC');


  // Modals state
  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [foodToEdit, setFoodToEdit] = useState<FoodResponseDto | null>(null);
  const [editData, setEditData] = useState<any>(null);

  const [isDetailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState<FoodDetailDto | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [isSiteContentDialogOpen, setSiteContentDialogOpen] = useState(false);
  const [siteContentFood, setSiteContentFood] = useState<FoodResponseDto | null>(null);

  const [isConfirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [foodToDelete, setFoodToDelete] = useState<FoodResponseDto | null>(null);

  const [isMergeModalOpen, setMergeModalOpen] = useState(false);
  const [foodToMerge, setFoodToMerge] = useState<FoodResponseDto | null>(null);
  const [systemFoods, setSystemFoods] = useState<FoodResponseDto[]>([]);
  const [systemFoodsLoading, setSystemFoodsLoading] = useState(false);

  // Import state
  const [isImportModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState<ImportFoodsResponseDto | null>(null);
  const [importHistory, setImportHistory] = useState<ImportSessionDto[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [isRevertConfirmOpen, setRevertConfirmOpen] = useState(false);
  const [sessionToRevert, setSessionToRevert] = useState<ImportSessionDto | null>(null);

  // Options state
  const [options, setOptions] = useState<FoodOptionsResponseDto>({ categories: [], measurementUnits: [], experts: [], sourceTypes: [] });
  // const [optionsLoading, setOptionsLoading] = useState(false);

  // Save filters to localStorage
  useEffect(() => {
    const stateToSave = {
      page,
      searchQuery,
      categoryFilter,
      sourceTypeFilter,
      expertFilter,
      structuredFilter,
      sitePublishedFilter,
      sortBy,
      sortOrder
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stateToSave));
  }, [page, searchQuery, categoryFilter, sourceTypeFilter, expertFilter, structuredFilter, sitePublishedFilter, sortBy, sortOrder]);

  // Fetch options on mount
  useEffect(() => {
    const fetchOptions = async () => {
      // setOptionsLoading(true);
      try {
        const data = await adminFoodService.getFoodOptions();
        setOptions(data);
      } catch (err) {
        console.error('Failed to fetch food options:', err);
        toastService.error('Не вдалося завантажити налаштування фільтрів.');
      } finally {
        // setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, []);

  const fetchFoods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: GetFoodsParams = {
        page,
        limit,
        search: debouncedSearchQuery || undefined,
        sortBy,
        sortOrder,
        category: categoryFilter ? (categoryFilter as FoodCategory) : undefined,
        sourceType: sourceTypeFilter,
        expertId: expertFilter !== '' ? (expertFilter as number) : undefined,
        isStructured: structuredFilter === 'all' ? undefined : structuredFilter === 'true',
        isSitePublished: sitePublishedFilter === 'all' ? undefined : sitePublishedFilter === 'true',
      };
      const response = await adminFoodService.getFoods(params);
      setFoods(response.data);
      if (response.meta && response.meta.total && response.meta.limit) {
        setTotalPages(Math.ceil(response.meta.total / response.meta.limit));
      } else {
        setTotalPages(0);
      }
    } catch (err) {
      toastService.error('Не вдалося завантажити список продуктів.');
      setError('Не вдалося завантажити список продуктів.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearchQuery, categoryFilter, sourceTypeFilter, expertFilter, structuredFilter, sitePublishedFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchFoods();
  }, [fetchFoods]);

  const fetchSystemFoods = useCallback(async (search: string) => {
    setSystemFoodsLoading(true);
    try {
      const response = await adminFoodService.searchSystemFoods(search, 20);
      setSystemFoods(response);
    } catch (err) {
      toastService.error('Не вдалося завантажити системні продукти.');
    } finally {
      setSystemFoodsLoading(false);
    }
  }, []);

  const handlePageChange = (value: number) => setPage(value);
  const handleLimitChange = (value: number) => { setLimit(value); setPage(1); };
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };
  const handleCategoryFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setCategoryFilter(event.target.value);
    setPage(1);
  }, []);
  const handleSourceTypeFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setSourceTypeFilter(event.target.value as FoodSourceType);
    setPage(1);
  }, []);
  const handleExpertFilterChange = useCallback((event: SelectChangeEvent<number | ''>) => {
    setExpertFilter(event.target.value as number | '');
    setPage(1);
  }, []);
  const handleStructuredFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setStructuredFilter(event.target.value);
    setPage(1);
  }, []);

  const handleSitePublishedFilterChange = useCallback((event: SelectChangeEvent<string>) => {
    setSitePublishedFilter(event.target.value);
    setPage(1);
  }, []);

  const handleResetFilters = () => {
    setSearchQuery('');
    setCategoryFilter('');
    setSourceTypeFilter(FoodSourceType.ALL);
    setExpertFilter('');
    setStructuredFilter('all');
    setSitePublishedFilter('all');
    setPage(1);
    setSortBy('name');
    setSortOrder('ASC');
  };

  const handleOpenSiteContentDialog = (food: FoodResponseDto) => {
    setSiteContentFood(food);
    setSiteContentDialogOpen(true);
  };

  const handlePublishToggled = (foodId: number, newValue: boolean) => {
    setFoods((prev) =>
      prev.map((f) => (f.id === foodId ? { ...f, isSitePublished: newValue } : f))
    );
  };

  const handleSort = (property: string) => {
    const isAsc = sortBy === property && sortOrder === 'ASC';
    setSortOrder(isAsc ? 'DESC' : 'ASC');
    setSortBy(property as FoodSortBy);
  };

  const handleCreateFood = async (data: any) => {
    setLoading(true);
    try {
      await adminFoodService.createFood({
        nameI18n: data.nameI18n,
        category: data.category as FoodCategory,
        measurementUnit: data.measurementUnit as MeasurementUnit,
        caloriesPer100g: data.caloriesPer100g,
        proteinPer100g: data.proteinPer100g,
        fatPer100g: data.fatPer100g,
        carbsPer100g: data.carbsPer100g,
        fiberPer100g: data.fiberPer100g,
        gramsPerUnit: data.gramsPerUnit,
        defaultMeasurementValue: data.defaultMeasurementValue,
        externalLink: data.externalLink,
        imageId: data.imageId,
      });
      toastService.success('Продукт успішно створено!');
      setCreateModalOpen(false);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка створення продукту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = async (food: FoodResponseDto) => {
    setLoading(true);
    setFoodToEdit(food);
    try {
      const data = await adminFoodService.getFoodForEdit(food.id);
      setEditData(data);
      setEditModalOpen(true);
    } catch (err) {
      toastService.error('Не вдалося завантажити дані для редагування.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDetailModal = async (food: FoodResponseDto) => {
    setDetailLoading(true);
    setDetailModalOpen(true);
    try {
      const data = await adminFoodService.getFoodDetail(food.id);
      setDetailData(data);
    } catch (err) {
      toastService.error('Не вдалося завантажити деталі продукту.');
      setDetailModalOpen(false);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleEditFromDetail = () => {
    if (detailData) {
      setDetailModalOpen(false);
      // We need to convert DetailDto to ResponseDto structure or just pass what we have if compatible
      // Since we fetch fresh data in handleOpenEditModal anyway, we can just pass an object with ID
      handleOpenEditModal({ id: detailData.id } as FoodResponseDto);
    }
  };

  const handleUpdateFood = async (data: any) => {
    if (!foodToEdit) return;

    setLoading(true);
    try {
      await adminFoodService.updateFood(foodToEdit.id, {
        nameI18n: data.nameI18n,
        category: data.category as FoodCategory,
        measurementUnit: data.measurementUnit as MeasurementUnit,
        caloriesPer100g: data.caloriesPer100g,
        proteinPer100g: data.proteinPer100g,
        fatPer100g: data.fatPer100g,
        carbsPer100g: data.carbsPer100g,
        fiberPer100g: data.fiberPer100g,
        gramsPerUnit: data.gramsPerUnit,
        defaultMeasurementValue: data.defaultMeasurementValue,
        externalLink: data.externalLink,
        imageId: data.imageId,
      });
      toastService.success('Продукт успішно оновлено!');
      setEditModalOpen(false);
      setFoodToEdit(null);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка оновлення продукту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDeleteConfirm = (food: FoodResponseDto) => {
    setFoodToDelete(food);
    setConfirmDeleteOpen(true);
  };

  const handleDeleteFood = async () => {
    if (!foodToDelete) return;
    setLoading(true);
    try {
      await adminFoodService.deleteFood(foodToDelete.id);
      toastService.success(`Продукт "${foodToDelete.name}" успішно видалено.`);
      setConfirmDeleteOpen(false);
      setFoodToDelete(null);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка видалення продукту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenMergeModal = (food: FoodResponseDto) => {
    setFoodToMerge(food);
    setSystemFoods([]);
    setMergeModalOpen(true);
  };

  const handleMergeFood = async (targetFood: FoodResponseDto) => {
    if (!foodToMerge) return;
    setLoading(true);
    try {
      const mergeDto: MergeFoodDto = {
        sourceFoodId: foodToMerge.id,
        targetFoodId: targetFood.id,
      };
      const result = await adminFoodService.mergeFoods(mergeDto);
      toastService.success(`Продукт '${result.removedFood.name}' об'єднано з '${result.targetFood.name}'. Оновлено подій: ${result.updatedCalendarEvents}.`);
      setMergeModalOpen(false);
      fetchFoods();
    } catch (err: any) {
      toastService.error(`Помилка злиття: ${err.message || 'Невідома помилка'}`);
    } finally {
      setLoading(false);
    }
  };

  // Import handlers
  const handleOpenImportModal = () => {
    setImportResult(null);
    setImportModalOpen(true);
  };

  const fetchImportHistory = async () => {
    setHistoryLoading(true);
    try {
      const history = await adminFoodService.getImportHistory();
      setImportHistory(history);
    } catch (err) {
      console.error('Помилка завантаження історії:', err);
      toastService.error('Не вдалося завантажити історію імпортів.');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const blob = await adminFoodService.downloadImportTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'foods_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Помилка завантаження шаблону:', err);
      toastService.error('Не вдалося завантажити шаблон.');
    }
  };

  const handleImportFoods = async (file: File) => {
    setImportLoading(true);
    setImportResult(null);
    try {
      const result = await adminFoodService.importFoods(file);
      setImportResult(result);
      if (result.count > 0) {
        toastService.success(`Успішно імпортовано ${result.count} продуктів. Сесія #${result.sessionId}`);
        fetchFoods(); // Refresh list
      }
      if (result.errors.length > 0) {
        toastService.warning(`Імпорт завершено з ${result.errors.length} помилками.`);
      }
    } catch (err: any) {
      console.error('Помилка імпорту:', err);
      toastService.error(`Помилка імпорту: ${err.message || 'Невідома помилка'}`);
    } finally {
      setImportLoading(false);
    }
  };

  const handleOpenRevertConfirm = (session: ImportSessionDto) => {
    setSessionToRevert(session);
    setRevertConfirmOpen(true);
  };

  const handleRevertImport = async () => {
    if (!sessionToRevert) return;
    
    setHistoryLoading(true); // Reuse history loading state or create a new one
    try {
      await adminFoodService.revertImport(sessionToRevert.id);
      toastService.success(`Імпорт #${sessionToRevert.id} успішно скасовано.`);
      setRevertConfirmOpen(false);
      setSessionToRevert(null);
      fetchImportHistory(); // Refresh history
      fetchFoods(); // Refresh food list as items were deleted
    } catch (err: any) {
      console.error('Помилка відкату:', err);
      toastService.error(`Помилка відкату: ${err.message || 'Невідома помилка'}`);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Helper to get label for category
  const getCategoryLabel = (value: string) => {
    const option = options.categories.find(
      (o: { value: string }) => o.value === value
    );
    return option ? option.label : value;
  };

  // Helper to get label for measurement unit
  const getMeasurementUnitLabel = (value: string) => {
    const option = options.measurementUnits.find(
      (o: { value: string }) => o.value === value
    );
    return option ? option.label : value;
  };

  const columns: Column<FoodResponseDto>[] = [
    { 
      id: 'name', 
      label: 'Назва', 
      sortable: true,
      render: (food) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <Avatar 
              variant="rounded" 
              src={food.imageUrl ? getImageUrl(food.imageUrl) : undefined} 
              alt={food.name}
              sx={{ width: 40, height: 40 }}
            >
              <ImageIcon />
            </Avatar>
            {food.legacyImg && (
              <Tooltip title="Є legacy зображення">
                <Box 
                  component="img"
                  src={food.legacyImg}
                  sx={{ 
                    position: 'absolute', 
                    bottom: -5, 
                    right: -5, 
                    width: 20, 
                    height: 20, 
                    borderRadius: '50%', 
                    border: '1px solid white',
                    zIndex: 1,
                    cursor: 'pointer',
                    objectFit: 'cover',
                    bgcolor: 'background.paper'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(food.legacyImg!, '_blank');
                  }}
                />
              </Tooltip>
            )}
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography variant="body2">{food.name}</Typography>
              {food.isSystem && (
                <Tooltip title="Системний елемент">
                  <VerifiedIcon color="primary" sx={{ fontSize: 16 }} />
                </Tooltip>
              )}
            </Box>
            <Box sx={{ mt: 0.5 }}>
              <TranslationStatus availableTranslations={food.availableTranslations} />
            </Box>
          </Box>
        </Box>
      )
    },
    { id: 'category', label: 'Категорія', sortable: true, render: (food) => getCategoryLabel(food.category) },
    { id: 'measurementUnit', label: 'Одиниця виміру', render: (food) => getMeasurementUnitLabel(food.measurementUnit) },
    { id: 'caloriesPer100g', label: 'Ккал/100г' },
    { 
      id: 'info', 
      label: 'Інфо', 
      align: 'center',
      render: (food) => (
        <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
          <Tooltip title={food.isStructured ? "Структурований" : "Неструктурований"}>
            {food.isStructured ? <StructuredIcon color="success" fontSize="small" /> : <UnstructuredIcon color="disabled" fontSize="small" />}
          </Tooltip>
          
          <Tooltip title={`Використано ${food.usageCount} разів`}>
            <Chip 
              icon={<VisibilityIcon fontSize="small" />} 
              label={food.usageCount} 
              size="small" 
              variant="outlined" 
            />
          </Tooltip>

          <Tooltip title={food.externalLink || "Посилання відсутнє"}>
            <span>
              <IconButton 
                href={food.externalLink || undefined} 
                target="_blank" 
                size="small" 
                color={food.externalLink ? "primary" : "default"}
                disabled={!food.externalLink}
                component={food.externalLink ? "a" : "button"}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )
    },
    {
      id: 'isSitePublished',
      label: 'На сайті',
      align: 'center',
      render: (food) => (
        <Tooltip title={food.isSitePublished ? 'Опубліковано — натисніть для редагування' : 'Не опубліковано — натисніть для налаштування'}>
          <Chip
            icon={<SiteIcon fontSize="small" />}
            label={food.isSitePublished ? 'Опубл.' : 'Ні'}
            size="small"
            color={food.isSitePublished ? 'success' : 'default'}
            variant={food.isSitePublished ? 'filled' : 'outlined'}
            onClick={(e) => { e.stopPropagation(); handleOpenSiteContentDialog(food); }}
            sx={{ cursor: 'pointer' }}
          />
        </Tooltip>
      ),
    },
    {
      id: 'createdByUserId',
      label: 'Джерело',
      render: (food) => (
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">
            {food.createdByUserId ? 'Користувач' : 'Система'}
          </Typography>
          {food.legacyId && (
            <>
              {food.legacyEntity === 'dish' ? (
                <Tooltip title={`Імпортовано з легасі страв (ID: ${food.legacyId})`}>
                  <RestaurantIcon color="action" fontSize="small" />
                </Tooltip>
              ) : (
                <Tooltip title={`Імпортовано з легасі продуктів (ID: ${food.legacyId})`}>
                  <StorageIcon color="action" fontSize="small" />
                </Tooltip>
              )}
            </>
          )}
        </Stack>
      ) 
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Button variant="outlined" startIcon={<UploadIcon />} onClick={handleOpenImportModal} sx={{ mr: 2 }}>Імпорт (Excel)</Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateModalOpen(true)}>Додати продукт</Button>
        </Box>
      </Box>

      <LibraryFilterPanel
        searchQuery={searchQuery}
        onSearchChange={handleSearchChange}
        mainFilterValue={categoryFilter}
        onMainFilterChange={handleCategoryFilterChange}
        mainFilterOptions={options.categories}
        mainFilterLabel="Категорія"
        sourceFilterValue={sourceTypeFilter}
        onSourceFilterChange={handleSourceTypeFilterChange}
        sourceFilterOptions={options.sourceTypes}
        expertFilterValue={expertFilter}
        onExpertFilterChange={handleExpertFilterChange}
        expertOptions={options.experts}
        structuredFilterValue={structuredFilter}
        onStructuredFilterChange={handleStructuredFilterChange}
        onResetFilters={handleResetFilters}
      >
        <FormControl sx={{ minWidth: '150px', flexGrow: 1 }} size="small">
          <InputLabel>Публікація</InputLabel>
          <Select value={sitePublishedFilter} label="Публікація" onChange={handleSitePublishedFilterChange}>
            <MenuItem value="all"><em>Всі</em></MenuItem>
            <MenuItem value="true">Опубліковані</MenuItem>
            <MenuItem value="false">Не опубліковані</MenuItem>
          </Select>
        </FormControl>
      </LibraryFilterPanel>

      <LibraryTable
        data={foods}
        columns={columns}
        loading={loading}
        error={error}
        page={page}
        totalPages={totalPages}
        onPageChange={handlePageChange}
        limit={limit}
        onLimitChange={handleLimitChange}
        storageKey="pagination:admin-food"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={handleSort}
        onEdit={handleOpenEditModal}
        onDelete={handleOpenDeleteConfirm}
        onMerge={handleOpenMergeModal}
        onRowClick={handleOpenDetailModal}
        isUserCreated={(food) => !!food.createdByUserId}
      />

      <FoodDialog
        open={isCreateModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onSubmit={handleCreateFood}
        options={options}
        isLoading={loading}
        title="Додати новий продукт"
        submitLabel="Створити"
      />

      <FoodDialog
        open={isEditModalOpen}
        onClose={() => setEditModalOpen(false)}
        onSubmit={handleUpdateFood}
        initialData={editData}
        options={options}
        isLoading={loading}
        title="Редагувати продукт"
        submitLabel="Зберегти зміни"
      />

      <FoodDetailDialog
        open={isDetailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        onEdit={handleEditFromDetail}
        data={detailData}
        isLoading={detailLoading}
        getCategoryLabel={getCategoryLabel}
        getMeasurementUnitLabel={getMeasurementUnitLabel}
      />

      <ConfirmationDialog 
        open={isConfirmDeleteOpen} 
        onClose={() => setConfirmDeleteOpen(false)} 
        onConfirm={handleDeleteFood} 
        title="Підтвердити видалення" 
        message={`Ви впевнені, що хочете видалити продукт "${foodToDelete?.name}"?`} 
        confirmText="Видалити" 
      />

      <MergeDialog
        open={isMergeModalOpen}
        onClose={() => setMergeModalOpen(false)}
        onMerge={handleMergeFood}
        sourceItem={foodToMerge}
        searchOptions={systemFoods}
        onSearch={fetchSystemFoods}
        isLoading={loading}
        isSearchLoading={systemFoodsLoading}
        getOptionLabel={(option) => `${option.name} (${getCategoryLabel(option.category)})`}
        entityName="продукт"
      />

      <ImportDialog
        open={isImportModalOpen}
        onClose={() => setImportModalOpen(false)}
        onDownloadTemplate={handleDownloadTemplate}
        onImport={handleImportFoods}
        onFetchHistory={fetchImportHistory}
        onRevert={handleOpenRevertConfirm}
        history={importHistory}
        importResult={importResult}
        isLoading={importLoading}
        isHistoryLoading={historyLoading}
        entityName="продуктів"
      />

      <ConfirmationDialog
        open={isRevertConfirmOpen}
        onClose={() => setRevertConfirmOpen(false)}
        onConfirm={handleRevertImport}
        title="Скасувати імпорт?"
        message={`Ви впевнені, що хочете скасувати імпорт #${sessionToRevert?.id}? Всі ${sessionToRevert?.importedCount} продуктів, створених під час цього імпорту, будуть безповоротно видалені.`}
        confirmText="Скасувати імпорт"
      />

      <FoodSiteContentDialog
        open={isSiteContentDialogOpen}
        onClose={() => setSiteContentDialogOpen(false)}
        food={siteContentFood}
        onPublishToggled={handlePublishToggled}
      />
    </Box>
  );
};
